/**
 * Library-item thumbnail rendering (CC_05 Block 3, D-04-konform).
 *
 * The element library stores raw `.excalidrawlib` elements — many in the OLD
 * Excalidraw schema (`strokeSharpness` instead of `roundness`, no fractional
 * index, …). `restoreElements` migrates them to the current schema before
 * `exportToSvg` turns them into a thumbnail. Both are public host-side APIs
 * (`@excalidraw/excalidraw`); nothing under packages/ is touched.
 *
 * The pure element→SVG-string step lives here so it can be unit-tested without a
 * React render or an IntersectionObserver; the lazy component that uses it lives
 * in LibraryPreview.tsx.
 */

import { exportToSvg, restoreElements } from "@excalidraw/excalidraw";

import type { ExcalidrawElement } from "@excalidraw/element/types";

/**
 * Render library elements to an SVG markup string, or "" when there is nothing
 * to draw. Restores stale-schema elements first, so an old package renders the
 * same as a freshly-created one.
 */
export async function renderItemSvg(elements: unknown[]): Promise<string> {
  const restored = restoreElements(
    (elements || []) as ExcalidrawElement[],
    null,
  );
  if (restored.length === 0) {
    return "";
  }
  const svg = await exportToSvg({
    elements: restored as never,
    files: {} as never,
    // Fonts would otherwise be fetched over the network; a thumbnail doesn't
    // need them and the CSP forbids it.
    skipInliningFonts: true,
  } as never);
  svg.setAttribute("width", "100%");
  svg.setAttribute("height", "100%");
  return new XMLSerializer().serializeToString(svg);
}
